<template>
  <div class="container-fluid">
    <Toolbar class="m-2">
      <template #start>
        <h2>{{ $t('nav.site') }}</h2>
      </template>
      <template #end>
        <Button
          :label="$t('sitepage.create')"
          icon="pi pi-plus"
          class=" mr-2 p-button-outlined p-button-rounded"
          @click="toSiteForm()"
        />
      </template>
    </Toolbar>

    <DataTable
      v-model:sortField="siteFilter.pageable.sortField"
      v-model:sortOrder="siteFilter.pageable.sortOrder"
      v-model:filters="siteFilter.filters"
      v-model:rows="siteFilter.pageable.size"
      striped-rows
      row-hover
      lazy
      paginator
      paginator-position="both"
      :total-records="total"
      :value="sites"
      :loading="isLoading"
      responsive-layout="scroll"
      filter-display="row"
      class="p-datatable-sm"
      @sort="getSites"
      @filter="getSites"
      @page="onPageChange($event)"
    >
      <Column
        :exportable="false"
        style="width: 175px; min-width: 175px;"
      >
        <template #body="{data}">
          <Button
            icon="pi pi-eye"
            class="p-button-rounded p-button-secondary mx-1"
            :title="$t('sitepage.title.detail')"
            @click="showSiteDetail(data)"
          />
          <Button
            icon="pi pi-pencil"
            class="p-button-rounded mx-1"
            :title="$t('action.crud.update')"
            @click="toSiteForm(data)"
          />
          <Button
            icon="pi pi-trash"
            class="p-button-rounded p-button-danger mx-1"
            :title="$t('sitepage.title.delete')"
            @click="openDeleteDialog(data)"
          />
        </template>
      </Column>
      <Column
        field="code"
        :header="$t('entity.site.code')"
        :sortable="true"
        :show-filter-menu="false"
      >
        <template #filter="{filterModel,filterCallback}">
          <InputText
            v-model="filterModel.value"
            type="text"
            class="p-column-filter"
            @keydown.enter="filterCallback()"
          />
        </template>
        <template #body="{data}">
          {{ data.code }}
        </template>
      </Column>
      <Column
        field="name"
        :header="$t('entity.site.name')"
        :sortable="true"
        :show-filter-menu="false"
      >
        <template #filter="{filterModel,filterCallback}">
          <InputText
            v-model="filterModel.value"
            type="text"
            class="p-column-filter"
            @keydown.enter="filterCallback()"
          />
        </template>
        <template #body="{data}">
          {{ data.name }}
        </template>
      </Column>
      <Column
        field="cluster"
        :header="$t('entity.site.cluster')"
        :sortable="true"
        :show-filter-menu="false"
      >
        <template #filter="{filterModel,filterCallback}">
          <InputText
            v-model="filterModel.value"
            type="text"
            class="p-column-filter"
            @keydown.enter="filterCallback()"
          />
        </template>
        <template #body="{data}">
          {{ data.cluster }}
        </template>
      </Column>
      <Column
        field="city"
        sort-field="address.city"
        :header="$t('entity.site.address.city')"
        :sortable="true"
        :show-filter-menu="false"
      >
        <template #filter="{filterModel,filterCallback}">
          <InputText
            v-model="filterModel.value"
            type="text"
            class="p-column-filter"
            @keydown.enter="filterCallback()"
          />
        </template>
        <template #body="{data}">
          {{ data.address.city }}
        </template>
      </Column>
      <Column
        field="postcode"
        sort-field="address.postcode"
        :header="$t('entity.site.address.postcode')"
        :sortable="true"
        :show-filter-menu="false"
      >
        <template #filter="{filterModel,filterCallback}">
          <InputText
            v-model="filterModel.value"
            type="text"
            class="p-column-filter"
            @keydown.enter="filterCallback()"
          />
        </template>
        <template #body="{data}">
          {{ data.address.postcode }}
        </template>
      </Column>
      <Column
        field="frenchDescription"
        style="width: 250px; white-space: pre-line; text-align: justify; word-break: break-word"
        :header="$t('entity.site.french_description')"
        :sortable="false"
        :show-filter-menu="false"
      >
        <template #body="{data}">
          <span>{{ data.frenchDescription }}</span>
        </template>
      </Column>
      <Column
        field="englishDescription"
        style="width: 250px; white-space: pre-line; text-align: justify; word-break: break-word"
        :header="$t('entity.site.english_description')"
        :sortable="false"
        :show-filter-menu="false"
      >
        <template #body="{data}">
          <span>{{ data.englishDescription }}</span>
        </template>
      </Column>
      <Column
        field="enabled"
        :header="$t('entity.site.enabled')"
        :sortable="false"
        :show-filter-menu="false"
        class="col-1"
      >
        <template #filter="{filterModel,filterCallback}">
          <TriStateCheckbox
            v-model="filterModel.value"
            @change="filterCallback()"
          />
        </template>

        <template #body="{data}">
          <div class="p-checkbox p-component">
            <Checkbox
              v-model="data.enabled"
              :binary="true"
              @click.prevent="openToggleDialog(data)"
            />
          </div>
        </template>
      </Column>
    </DataTable>

    <Dialog
      v-model:visible="toggleDialog"
      :modal="true"
    >
      <template #header>
        <h3 v-if="selected.enabled">
          {{ $t('sitepage.dialog.enable.header') }}
        </h3>
        <h3 v-else>
          {{ $t('sitepage.dialog.disable.header') }}
        </h3>
      </template>

      <div class="confirmation-content">
        <span v-if="selected.enabled">{{ $t('sitepage.dialog.enable.content') }}</span>
        <span v-else>{{ $t('sitepage.dialog.disable.content') }}</span>
      </div>

      <template #footer>
        <Button
          :label="$t('sitepage.dialog.abortButton')"
          icon="pi pi-times"
          class="p-button-secondary p-button-rounded p-button-outlined"
          @click="abortToggle()"
        />
        <Button
          :label="$t('sitepage.dialog.confirmButton')"
          icon="pi pi-check"
          class="p-button-rounded p-button-outlined"
          @click="toggleEnabled(!!selected.enabled)"
        />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="deleteDialog"
      :modal="true"
    >
      <template #header>
        <h3>{{ $t('sitepage.dialog.delete.header') }}</h3>
      </template>

      <div class="confirmation-content">
        <span>{{ $t('sitepage.dialog.delete.content') }}</span>
      </div>
      <template #footer>
        <Button
          :label="$t('sitepage.dialog.abortButton')"
          icon="pi pi-times"
          class="p-button-secondary p-button-rounded p-button-outlined"
          @click="deleteDialog = false"
        />
        <Button
          :label="$t('sitepage.dialog.confirmButton')"
          icon="pi pi-check"
          class="p-button-success mr-2 p-button-outlined p-button-rounded"
          @click="deleteSite(selected)"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import Toolbar from 'primevue/toolbar';
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import InputText from "primevue/inputtext";
import Column from "primevue/column";
import TriStateCheckbox from "primevue/tristatecheckbox";
import DataTable from "primevue/datatable";
import {reactive} from "vue";
import Dialog from "primevue/dialog";
import useSiteFilter from "@/composables/filters/useSiteFilter";

export default {
  name: 'SiteTable',
  components: {Dialog, DataTable, TriStateCheckbox, Column, InputText, Checkbox, Toolbar, Button},
  setup() {
    const siteFilter = reactive(useSiteFilter());
    return { siteFilter };
  },
  data() {
    return {
      sites: [],
      total: 0,
      isLoading: false,
      selected: null,
      toggleDialog: false,
      deleteDialog: false
    }
  },
  async mounted() {
    await this.getSites();
  },
  methods: {
    async getSites() {
      this.isLoading = true;
      try {
        const { content, totalElements } = await this.$siteService.fetchSitesPagingAndSorting(this.siteFilter.getQueryParams());
        this.sites = content;
        this.total = totalElements;
      } catch (e) {
        console.error(e);
        this.$emitter.emit('toast', { severity: 'error', message: 'error.global' });
      } finally {
        this.isLoading = false;
      }
    },
    onPageChange(event) {
      this.siteFilter.pageable.page = event.page;
      this.getSites();
    },
    toSiteForm(site) {
      if (site) {
        this.$router.push({name: 'siteUpdate', params: {id: site.id}})
      } else {
        this.$router.push({name: 'siteCreate'})
      }
    },
    showSiteDetail(site) {
      this.$router.push({ name: 'siteDetail', params: { id: site.id } })
    },
    openToggleDialog(site) {
      this.selected = site;
      this.toggleDialog = true;
    },
    openDeleteDialog(site) {
      this.selected = site;
      this.deleteDialog = true;
    },
    abortToggle() {
      this.selected.enabled = !this.selected.enabled;
      this.selected = null;
      this.toggleDialog = false;
    },
    async toggleEnabled(value) {
      try {
        await this.$siteService.updateStatus(this.selected, value);
        if (value) {
          this.$emitter.emit('toast', {severity: 'success', message: 'sitepage.toast.enableSuccess'});
        } else {
          this.$emitter.emit('toast', {severity: 'success', message: 'sitepage.toast.disableSuccess'});
        }
        await this.getSites();
      } catch (e) {
        const errors = e.response.data.map(error => this.$t(error));
        this.$emitter.emit('toast', {severity: 'error', message: errors.join(', ')});
      } finally {
        this.selected = null;
        this.toggleDialog = false;
      }
    },
    async deleteSite() {
      try {
        await this.$siteService.deleteSite(this.selected);
        this.$emitter.emit('toast', {severity: 'success', message: 'sitepage.toast.deleteSuccess'});
        await this.getSites()
      } catch (e) {
        const errors = e.response.data.map(error => this.$t(error));
        this.$emitter.emit('toast', {severity: 'error', message: errors.join(', ')});
      } finally {
        this.selected = null;
        this.deleteDialog = false;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.pi-check {
  color: white;
}
</style>
